import filter from 'lodash/filter';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';
import find from 'lodash/find';

import {
  createSelector as createMemoizedSelector
} from 'reselect';

import {
  FETCH_NOTIFICATION_SETTINGS_REQUEST,
  FETCH_NOTIFICATION_SETTINGS_SUCCESS,
  FETCH_NOTIFICATION_SETTINGS_FAILURE,
  CREATE_NOTIFICATION_SETTING_SUCCESS,
  DELETE_NOTIFICATION_SETTING_SUCCESS
} from '../const/action_types.js';

import {
  NOTIFICATIONS_SETTINGS_ROUTE_VISIT_EVENT_TYPE,
  NOTIFICATIONS_SETTINGS_APPROVAL_REQUEST_EVENT_TYPE,
  NOTIFICATIONS_SETTINGS_SITE_ASSIGNMENT_EVENT_TYPE,
  NOTIFICATIONS_SETTINGS_ACCESS_REQUEST_EVENT_TYPE,
  NOTIFICATIONS_SETTINGS_FORM_RESPONSE_EVENT_TYPE
} from '../const/notification_settings_event_types.js';

export default (state = {}, { type, ...action })=> {

  switch (type) {

  case FETCH_NOTIFICATION_SETTINGS_REQUEST: {
    return {
      ...state,
      isFetchingNotifications: true
    };
  }

  case FETCH_NOTIFICATION_SETTINGS_SUCCESS: {
    return {
      ...state,
      notificationSettingsById: {
        ...state.notificationSettingsById,
        ...keyBy(action.data, 'id')
      },
      isFetchingNotifications: false
    };
  }

  case FETCH_NOTIFICATION_SETTINGS_FAILURE: {
    return {
      ...state,
      isFetchingNotifications: false
    };
  }

  case CREATE_NOTIFICATION_SETTING_SUCCESS: {
    return {
      ...state,
      notificationSettingsById: {
        ...state.notificationSettingsById,
        [action.data.id]: action.data
      }
    };
  }

  case DELETE_NOTIFICATION_SETTING_SUCCESS: {
    return {
      ...state,
      notificationSettingsById: omit(state.notificationSettingsById, [action.notificationSettingId])
    };
  }

  }

  return state;
};

export const getIsFetchingNotificationSettings = (state)=>
  !!state.isFetchingNotifications;

export const getNotificationSettingsById = (state)=>
  state.notificationSettingsById;

export const getNotificationSettingsByEventType = createMemoizedSelector(
  getNotificationSettingsById,
  notificationSettingsById=>
    groupBy(notificationSettingsById, 'event_type')
);

// selectors for specific event types

export const getRouteVisitNotificationSettings = createMemoizedSelector(
  getNotificationSettingsByEventType,
  notificationSettingsByEventType=>
    notificationSettingsByEventType[NOTIFICATIONS_SETTINGS_ROUTE_VISIT_EVENT_TYPE] || []
);

export const getSiteApprovalRequestNotificationSettings = createMemoizedSelector(
  getNotificationSettingsByEventType,
  notificationSettingsByEventType=>
    notificationSettingsByEventType[NOTIFICATIONS_SETTINGS_APPROVAL_REQUEST_EVENT_TYPE] || []
);

export const getSiteAssignmentNotificationSettings = createMemoizedSelector(
  getNotificationSettingsByEventType,
  notificationSettingsByEventType=>
    notificationSettingsByEventType[NOTIFICATIONS_SETTINGS_SITE_ASSIGNMENT_EVENT_TYPE] || []
);

export const getSiteAccessRequestNotificationSettings = createMemoizedSelector(
  getNotificationSettingsByEventType,
  notificationSettingsByEventType=>
    notificationSettingsByEventType[NOTIFICATIONS_SETTINGS_ACCESS_REQUEST_EVENT_TYPE] || []
);

export const getFormResponseNotificationSettings = createMemoizedSelector(
  getNotificationSettingsByEventType,
  notificationSettingsByEventType=>
    notificationSettingsByEventType[NOTIFICATIONS_SETTINGS_FORM_RESPONSE_EVENT_TYPE] || []
);

// selectors for specific event types and object ids

export const getRouteVisitNotificationSettingsForRoute = (state, routeId)=>
  filter(getRouteVisitNotificationSettings(state), ['object_id', routeId]);

export const getSiteApprovalRequestNotificationSettingsForSite = (state, siteId)=>
  filter(getSiteApprovalRequestNotificationSettings(state), ['object_id', siteId]);

export const getSiteAssignmentNotificationSettingsForSite = (state, siteId)=>
  filter(getSiteAssignmentNotificationSettings(state), ['object_id', siteId]);

export const getSiteAccessRequestNotificationSettingsForSite = (state, siteId)=>
  filter(getSiteAccessRequestNotificationSettings(state), ['object_id', siteId]);

export const getFormResponseNotificationSettingsForForm = (state, formId)=>
  filter(getFormResponseNotificationSettings(state), ['object_id', formId]);

// selectors about the current user and their subscriptions

export const getIsUserSubscribedToEventTypeForObjectId = (state, userId, eventType, objectId)=> {
  var filteredSettings;

  if (eventType === NOTIFICATIONS_SETTINGS_ROUTE_VISIT_EVENT_TYPE) {
    filteredSettings = getRouteVisitNotificationSettingsForRoute(state, objectId);

  } else if (eventType === NOTIFICATIONS_SETTINGS_APPROVAL_REQUEST_EVENT_TYPE) {
    filteredSettings = getSiteApprovalRequestNotificationSettingsForSite(state, objectId);

  } else if (eventType === NOTIFICATIONS_SETTINGS_SITE_ASSIGNMENT_EVENT_TYPE) {
    filteredSettings = getSiteAssignmentNotificationSettingsForSite(state, objectId);

  } else if (eventType === NOTIFICATIONS_SETTINGS_ACCESS_REQUEST_EVENT_TYPE) {
    filteredSettings = getSiteAccessRequestNotificationSettingsForSite(state, objectId);

  } else if (eventType === NOTIFICATIONS_SETTINGS_FORM_RESPONSE_EVENT_TYPE) {
    filteredSettings = getFormResponseNotificationSettingsForForm(state, objectId);

  }

  return !!find(filteredSettings, ['user_id', userId]);
};

export const getIsUserSubscribedToRouteVisitNotificationsForRoute = (state, userId, routeId)=>
  getIsUserSubscribedToEventTypeForObjectId(
    state,
    userId,
    NOTIFICATIONS_SETTINGS_ROUTE_VISIT_EVENT_TYPE,
    routeId
  );

export const getIsUserSubscribedToSiteApprovalRequestNotificationsForSite = (state, userId, siteId)=>
  getIsUserSubscribedToEventTypeForObjectId(
    state,
    userId,
    NOTIFICATIONS_SETTINGS_APPROVAL_REQUEST_EVENT_TYPE,
    siteId
  );

export const getIsUserSubscribedToSiteAssignmentNotificationsForSite = (state, userId, siteId)=>
  getIsUserSubscribedToEventTypeForObjectId(
    state,
    userId,
    NOTIFICATIONS_SETTINGS_SITE_ASSIGNMENT_EVENT_TYPE,
    siteId
  );

export const getIsUserSubscribedToSiteAccessRequestNotificationsForSite = (state, userId, siteId)=>
  getIsUserSubscribedToEventTypeForObjectId(
    state,
    userId,
    NOTIFICATIONS_SETTINGS_ACCESS_REQUEST_EVENT_TYPE,
    siteId
  );

export const getIsUserSubscribedToFormResponseNotificationsForForm = (state, userId, formId)=>
  getIsUserSubscribedToEventTypeForObjectId(
    state,
    userId,
    NOTIFICATIONS_SETTINGS_FORM_RESPONSE_EVENT_TYPE,
    formId
  );

// misc selectors

export const getNotificationSettingsForUserByRouteId = (state, userId)=>
  groupBy(
    filter(getRouteVisitNotificationSettings(state), ['user_id', userId]),
    'object_id'
  );
